/**
 * ADMIN DASHBOARD STATS - Liczniki statusu pracy
 *
 * Liczniki (Jeszcze nie zaczęli / W trakcie / Zakończyli) są obliczane z mapLocations
 * – tej samej tablicy co mapa. Liczymy BUDYNKI (nieruchomości), nie pracowników.
 *
 * Mapowanie statusów mapy:
 * - red → Jeszcze nie zaczęli (zaległe)
 * - yellow, orange → W trakcie
 * - green → Zakończyli
 * - gray, hidden → pomijane (nie wliczane do żadnej kategorii)
 *
 * Zobacz: docs/AUDIT_DASHBOARD_STATS_COUNTERS.md
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRole } from "@/contexts/RoleContext";
import { useQueryClient } from "@tanstack/react-query";
import {
  adminDashboardQueryKey,
  orgCommandCenterSettingsKey,
  useAdminDashboardData,
  useOrgCommandCenterSettings,
  type OrgCommandCenterSettings,
} from "@/hooks/useAdminDashboardData";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Building2, UserPlus, ListTodo, Settings, Wallet } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import type { AdminDashboardSnapshot } from "@/hooks/adminDashboardFetch";
import { AdminDashboardStats } from "@/components/admin/AdminDashboardStats";
import { AdminDashboardQuickNav } from "@/components/admin/AdminDashboardQuickNav";
import { AdminDashboardMapCard } from "@/components/admin/AdminDashboardMapCard";
import { AdminDashboardAlerts } from "@/components/admin/AdminDashboardAlerts";
import { AdminDashboardAutoCheckouts } from "@/components/admin/AdminDashboardAutoCheckouts";
import { useRecentAutoCheckouts } from "@/hooks/useRecentAutoCheckouts";
import { clampAutoCheckoutDistanceMeters, DEFAULT_AUTO_CHECKOUT_DISTANCE_METERS } from "@/lib/geoDistance";
import { AdminDashboardActivity } from "@/components/admin/AdminDashboardActivity";
import {
  NewPropertyModal,
  NewWorkerModal,
  OneTimeTaskModal,
} from "@/components/admin/AdminDashboardModals";
import { WarehouseManagement } from "@/components/admin/WarehouseManagement";
import { FinanceSection } from "@/components/admin/FinanceSection";
import { ExtraPaidJobModal } from "@/components/admin/ExtraPaidJobModal";
import { ExtraPaidApprovalQueue } from "@/components/admin/ExtraPaidApprovalQueue";
import { useCanViewOrgFinancials } from "@/hooks/useCanViewOrgFinancials";
import { EquipmentInventoryPanel } from "@/components/equipment/EquipmentInventoryPanel";
import { EquipmentRejectionBanner } from "@/components/equipment/EquipmentRejectionBanner";
import { AdminDashboardIssues } from "@/components/admin/AdminDashboardIssues";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { SearchableSelectOption } from "@/components/admin/SearchableSelect";
import { useTranslation } from "react-i18next";

const VALID_TABS = ['dashboard', 'warehouse', 'finance', 'equipment'] as const;
type TabValue = (typeof VALID_TABS)[number];

export default function AdminDashboard() {
  const { t } = useTranslation();
  const { currentOrgId, isLoading: isRoleLoading, currentRole, refetchOrgSettings } = useRole();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canViewOrgFinancials = useCanViewOrgFinancials();
  const tabFromUrl = searchParams.get('tab');
  const activeTab: TabValue = VALID_TABS.includes(tabFromUrl as TabValue)
    ? (tabFromUrl as TabValue)
    : 'dashboard';

  const handleTabChange = useCallback(
    (value: string) => {
      if (value === "finance" && !canViewOrgFinancials) return;
      if (value === 'dashboard') {
        setSearchParams((p) => {
          const next = new URLSearchParams(p);
          next.delete('tab');
          return next;
        });
      } else {
        setSearchParams((p) => {
          const next = new URLSearchParams(p);
          next.set('tab', value);
          return next;
        });
      }
    },
    [setSearchParams, canViewOrgFinancials]
  );
  const [propertyValue, setPropertyValue] = useState("");
  const [workerValue, setWorkerValue] = useState("");
  const [visibleAlertsCount, setVisibleAlertsCount] = useState(10);
  const [newPropertyOpen, setNewPropertyOpen] = useState(false);
  const [newWorkerOpen, setNewWorkerOpen] = useState(false);
  const [oneTimeTaskOpen, setOneTimeTaskOpen] = useState(false);
  const [extraPaidOpen, setExtraPaidOpen] = useState(false);
  const [taskGenerationDays, setTaskGenerationDays] = useState<number>(15);
  const [autoCheckoutDistanceMeters, setAutoCheckoutDistanceMeters] = useState<number>(DEFAULT_AUTO_CHECKOUT_DISTANCE_METERS);
  const [isUpdatingFinancials, setIsUpdatingFinancials] = useState(false);
  const [isUpdatingSnowSeason, setIsUpdatingSnowSeason] = useState(false);
  const [isUpdatingTaskGenerationDays, setIsUpdatingTaskGenerationDays] = useState(false);
  const [isAutomationDialogOpen, setIsAutomationDialogOpen] = useState(false);
  const [showArchivedProperties, setShowArchivedProperties] = useState(false);

  const {
    data,
    isPending,
    refetch,
    loadMoreActivity,
    isLoadingMoreActivity,
  } = useAdminDashboardData(currentOrgId, isRoleLoading, showArchivedProperties);

  const { data: orgSettings } = useOrgCommandCenterSettings(
    currentOrgId,
    Boolean(currentOrgId) &&
      !isRoleLoading &&
      (currentRole === "owner" || currentRole === "coordinator")
  );

  const globalCoordinatorViewFinancials = orgSettings?.globalCoordinatorViewFinancials ?? false;
  const isSnowSeason = data?.isSnowSeason === true;

  useEffect(() => {
    if (orgSettings?.taskGenerationDays == null) return;
    setTaskGenerationDays(orgSettings.taskGenerationDays);
  }, [orgSettings?.taskGenerationDays]);

  useEffect(() => {
    if (orgSettings?.autoCheckoutDistanceMeters == null) return;
    setAutoCheckoutDistanceMeters(orgSettings.autoCheckoutDistanceMeters);
  }, [orgSettings?.autoCheckoutDistanceMeters]);

  const { data: autoCheckouts = [] } = useRecentAutoCheckouts(
    currentOrgId,
    Boolean(currentOrgId) &&
      !isRoleLoading &&
      (currentRole === "owner" || currentRole === "coordinator")
  );

  useEffect(() => {
    setVisibleAlertsCount(10);
  }, [currentOrgId, showArchivedProperties]);

  useEffect(() => {
    if (activeTab === "finance" && !canViewOrgFinancials) {
      setSearchParams((p) => {
        const next = new URLSearchParams(p);
        next.delete("tab");
        return next;
      });
    }
  }, [activeTab, canViewOrgFinancials, setSearchParams]);

  const stats = data?.stats ?? { notStarted: 0, inProgress: 0, done: 0 };
  const mapLocations = data?.mapLocations ?? [];
  const propertyOptions = data?.propertyOptions ?? [];
  const workerOptions = data?.workerOptions ?? [];
  const alerts = data?.alerts ?? [];
  const activity = data?.activity ?? [];
  const activityHasMore = data?.activityHasMore ?? false;
  const locations = data?.locations ?? [];
  const sectionsByLocation = data?.sectionsByLocation ?? {};
  const locationsWithoutCoords = data?.locationsWithoutCoords ?? [];

  const patchOrgSettings = (patch: Partial<OrgCommandCenterSettings>) => {
    if (!currentOrgId) return;
    queryClient.setQueryData<OrgCommandCenterSettings>(
      orgCommandCenterSettingsKey(currentOrgId),
      (prev) => (prev ? { ...prev, ...patch } : prev)
    );
  };

  const patchDashboardSnowSeason = (checked: boolean) => {
    if (!currentOrgId) return;
    queryClient.setQueryData<AdminDashboardSnapshot>(
      adminDashboardQueryKey(currentOrgId, showArchivedProperties),
      (prev) => (prev ? { ...prev, isSnowSeason: checked } : prev)
    );
  };

  const handleToggleGlobalCoordinatorFinancials = async (checked: boolean) => {
    if (!currentOrgId) return;

    const previous = orgSettings;
    patchOrgSettings({ globalCoordinatorViewFinancials: checked });
    setIsUpdatingFinancials(true);
    try {
      const { error } = await supabase
        .from("organizations")
        .update({ global_coordinator_view_financials: checked })
        .eq("id", currentOrgId);

      if (error) {
        console.error("Error updating global_coordinator_view_financials:", error);
        if (previous) {
          queryClient.setQueryData(orgCommandCenterSettingsKey(currentOrgId), previous);
        }
        toast.error(t("admin.dashboard.saveError"));
        return;
      }

      toast.success(t("admin.dashboard.saved"));
    } catch (error) {
      console.error("Unexpected error:", error);
      if (previous) {
        queryClient.setQueryData(orgCommandCenterSettingsKey(currentOrgId), previous);
      }
      toast.error(t("admin.dashboard.unexpectedError"));
    } finally {
      setIsUpdatingFinancials(false);
    }
  };

  const handleToggleSnowSeason = async (checked: boolean) => {
    if (!currentOrgId) return;

    const previousOrgSettings = orgSettings;
    const previousSnapshot = queryClient.getQueryData<AdminDashboardSnapshot>(
      adminDashboardQueryKey(currentOrgId, showArchivedProperties)
    );
    patchOrgSettings({ isSnowSeason: checked });
    patchDashboardSnowSeason(checked);
    setIsUpdatingSnowSeason(true);
    try {
      const { error } = await supabase
        .from("organizations")
        .update({ is_snow_season: checked })
        .eq("id", currentOrgId);

      if (error) {
        console.error("Error updating is_snow_season:", error);
        if (previousOrgSettings) {
          queryClient.setQueryData(orgCommandCenterSettingsKey(currentOrgId), previousOrgSettings);
        }
        if (previousSnapshot) {
          queryClient.setQueryData(
            adminDashboardQueryKey(currentOrgId, showArchivedProperties),
            previousSnapshot
          );
        }
        toast.error(t("admin.dashboard.saveError"));
        return;
      }

      await refetchOrgSettings();
      toast.success(t("admin.dashboard.saved"));
    } catch (error) {
      console.error("Unexpected error:", error);
      if (previousOrgSettings) {
        queryClient.setQueryData(orgCommandCenterSettingsKey(currentOrgId), previousOrgSettings);
      }
      if (previousSnapshot) {
        queryClient.setQueryData(
          adminDashboardQueryKey(currentOrgId, showArchivedProperties),
          previousSnapshot
        );
      }
      toast.error(t("admin.dashboard.unexpectedError"));
    } finally {
      setIsUpdatingSnowSeason(false);
    }
  };

  const handleSaveTaskGenerationDays = async () => {
    if (!currentOrgId) return;
    const value = Math.max(1, Math.min(365, taskGenerationDays));

    setIsUpdatingTaskGenerationDays(true);
    try {
      if (currentRole === "owner") {
        const { error } = await supabase
          .from("organizations")
          .update({ task_generation_days: value })
          .eq("id", currentOrgId);

        if (error) {
          console.error("Error updating task_generation_days:", error);
          toast.error(t("admin.dashboard.saveError"));
          return;
        }
      }

      const distance = clampAutoCheckoutDistanceMeters(autoCheckoutDistanceMeters);
      const { error: distanceError } = await supabase
        .from("organizations")
        .update({ auto_checkout_distance_meters: distance })
        .eq("id", currentOrgId);
      if (distanceError) {
        console.error("Error updating auto_checkout_distance_meters:", distanceError);
        toast.error(t("admin.dashboard.distanceSaveFailed"));
        return;
      }

      setTaskGenerationDays(value);
      setAutoCheckoutDistanceMeters(distance);
      patchOrgSettings({ taskGenerationDays: value, autoCheckoutDistanceMeters: distance });
      toast.success(t("admin.dashboard.saved"));
      setIsAutomationDialogOpen(false);
    } catch (error) {
      console.error("Unexpected error:", error);
      toast.error(t("admin.dashboard.unexpectedError"));
    } finally {
      setIsUpdatingTaskGenerationDays(false);
    }
  };

  if (!currentOrgId) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-center text-muted-foreground">
        {t("admin.dashboard.noOrganization")}
      </div>
    );
  }

  // Show full-page skeleton only on first load (no cached payload). Avoid flashing skeletons on tab switch / refetch when data exists.
  if (isPending && data === undefined) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-[320px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-2xl font-bold">{t("admin.dashboard.commandCenter")}</h2>
        <div className="flex flex-wrap items-center gap-4 ml-auto">
          {currentRole === "owner" && (
            <>
              <div className="flex items-center gap-2">
                <Label htmlFor="dashboard-global-financials" className="text-sm font-normal whitespace-nowrap cursor-pointer">
                  {t("admin.dashboard.financialsVisibility")}
                </Label>
                <Switch
                  id="dashboard-global-financials"
                  name="dashboard-global-financials"
                  checked={globalCoordinatorViewFinancials}
                  onCheckedChange={handleToggleGlobalCoordinatorFinancials}
                  disabled={isUpdatingFinancials}
                />
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="dashboard-snow-season" className="text-sm font-normal whitespace-nowrap cursor-pointer">
                  {t("admin.dashboard.snowSeason")}
                </Label>
                <Switch
                  id="dashboard-snow-season"
                  name="dashboard-snow-season"
                  checked={isSnowSeason}
                  onCheckedChange={handleToggleSnowSeason}
                  disabled={isUpdatingSnowSeason}
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsAutomationDialogOpen(true)}
                className="gap-2"
              >
                <Settings className="h-4 w-4" />
                {t("admin.dashboard.configureAutomation")}
              </Button>
            </>
          )}
          {currentRole === "coordinator" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAutomationDialogOpen(true)}
              className="gap-2"
            >
              <Settings className="h-4 w-4" />
              {t("admin.dashboard.workTime")}
            </Button>
          )}
          <div className="flex items-center gap-2">
            <Label htmlFor="dashboard-show-archived" className="text-sm font-normal whitespace-nowrap cursor-pointer">
              {t("admin.dashboard.showArchived")}
            </Label>
            <Switch
              id="dashboard-show-archived"
              name="dashboard-show-archived"
              checked={showArchivedProperties}
              onCheckedChange={setShowArchivedProperties}
            />
          </div>
          <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setNewPropertyOpen(true)}
            className="gap-2"
          >
            <Building2 className="h-4 w-4" />
            {t("admin.dashboard.newProperty")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setNewWorkerOpen(true)}
            className="gap-2"
          >
            <UserPlus className="h-4 w-4" />
            {t("admin.dashboard.newWorker")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOneTimeTaskOpen(true)}
            className="gap-2"
          >
            <ListTodo className="h-4 w-4" />
            {t("admin.dashboard.oneTimeTask")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExtraPaidOpen(true)}
            className="gap-2"
          >
            <Wallet className="h-4 w-4" />
            {t("admin.dashboard.extraWork")}
          </Button>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList>
          <TabsTrigger value="dashboard">{t("admin.dashboard.tabDashboard")}</TabsTrigger>
          <TabsTrigger value="warehouse">{t("admin.dashboard.tabWarehouse")}</TabsTrigger>
          <TabsTrigger value="equipment">{t("admin.dashboard.tabEquipment")}</TabsTrigger>
          {canViewOrgFinancials && (
            <TabsTrigger value="finance">{t("admin.dashboard.tabFinance")}</TabsTrigger>
          )}
        </TabsList>

        <TabsContent
          value="dashboard"
          forceMount
          className="mt-6 space-y-6 outline-none data-[state=inactive]:hidden"
        >
          {(currentRole === "owner" || currentRole === "coordinator") && (
            <Dialog open={isAutomationDialogOpen} onOpenChange={setIsAutomationDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("admin.dashboard.automationTitle")}</DialogTitle>
                  <DialogDescription>
                    {t("admin.dashboard.automationDescription")}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {currentRole === "owner" && (
                    <div className="space-y-2">
                      <Label htmlFor="task-generation-days">{t("admin.dashboard.taskGenerationDays")}</Label>
                      <Input
                        id="task-generation-days"
                        type="number"
                        min={1}
                        max={365}
                        value={taskGenerationDays}
                        onChange={(e) => setTaskGenerationDays(parseInt(e.target.value, 10) || 15)}
                        disabled={isUpdatingTaskGenerationDays}
                        className="w-24"
                      />
                      <p className="text-sm text-muted-foreground">
                        {t("admin.dashboard.taskGenerationHint")}
                      </p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="auto-checkout-distance">{t("admin.dashboard.autoCheckoutMeters")}</Label>
                    <Input
                      id="auto-checkout-distance"
                      type="number"
                      min={50}
                      max={5000}
                      value={autoCheckoutDistanceMeters}
                      onChange={(e) => setAutoCheckoutDistanceMeters(parseInt(e.target.value, 10) || DEFAULT_AUTO_CHECKOUT_DISTANCE_METERS)}
                      disabled={isUpdatingTaskGenerationDays}
                      className="w-28"
                    />
                    <p className="text-sm text-muted-foreground">
                      {t("admin.dashboard.autoCheckoutHint")}
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setIsAutomationDialogOpen(false)}
                    disabled={isUpdatingTaskGenerationDays}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    onClick={handleSaveTaskGenerationDays}
                    disabled={isUpdatingTaskGenerationDays}
                  >
                    {isUpdatingTaskGenerationDays ? t("admin.dashboard.saving") : t("admin.dashboard.saveChanges")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          <AdminDashboardStats stats={stats} />

          <AdminDashboardQuickNav
            propertyOptions={propertyOptions}
            workerOptions={workerOptions}
            propertyValue={propertyValue}
            workerValue={workerValue}
            onPropertyChange={setPropertyValue}
            onWorkerChange={setWorkerValue}
          />

          <AdminDashboardMapCard
            orgId={currentOrgId}
            isSnowSeason={isSnowSeason}
            cleaningLocations={mapLocations}
            locationsWithoutCoords={locationsWithoutCoords}
            onDetails={(id) => navigate(`/properties/${id}`)}
          />

          <AdminDashboardIssues orgId={currentOrgId} />

          <ExtraPaidApprovalQueue orgId={currentOrgId} />

          <div className="grid gap-6 md:grid-cols-2">
            <AdminDashboardAutoCheckouts items={autoCheckouts} />
            <AdminDashboardAlerts
              alerts={alerts.slice(0, visibleAlertsCount)}
              hasMore={visibleAlertsCount < alerts.length}
              onLoadMore={() => setVisibleAlertsCount((prev) => prev + 10)}
            />
            <AdminDashboardActivity
              items={activity}
              hasMore={activityHasMore}
              onLoadMore={loadMoreActivity}
              isLoadingMore={isLoadingMoreActivity}
            />
          </div>

          {currentOrgId && <EquipmentRejectionBanner orgId={currentOrgId} />}
        </TabsContent>

        <TabsContent
          value="warehouse"
          forceMount
          className="mt-6 outline-none data-[state=inactive]:hidden"
        >
          <WarehouseManagement />
        </TabsContent>

        <TabsContent
          value="equipment"
          className="mt-6 outline-none"
        >
          <EquipmentInventoryPanel />
        </TabsContent>

        {canViewOrgFinancials && (
          <TabsContent
            value="finance"
            forceMount
            className="mt-6 outline-none data-[state=inactive]:hidden"
          >
            <FinanceSection />
          </TabsContent>
        )}
      </Tabs>

      <NewPropertyModal
        open={newPropertyOpen}
        onOpenChange={setNewPropertyOpen}
        orgId={currentOrgId}
        onSuccess={refetch}
      />
      <NewWorkerModal
        open={newWorkerOpen}
        onOpenChange={setNewWorkerOpen}
        orgId={currentOrgId}
        onSuccess={refetch}
      />
      <OneTimeTaskModal
        open={oneTimeTaskOpen}
        onOpenChange={setOneTimeTaskOpen}
        orgId={currentOrgId}
        locations={locations}
        sectionsByLocation={sectionsByLocation}
        onSuccess={refetch}
      />
      <ExtraPaidJobModal
        open={extraPaidOpen}
        onOpenChange={setExtraPaidOpen}
        orgId={currentOrgId}
        locations={locations}
        onSuccess={refetch}
      />
    </div>
  );
}
